import { useState, useEffect, useCallback, useRef } from 'react'
import CancelIconCircle from '@/components/ui/icons/cancel-icon-circle'
import { Dialog, DialogContent } from '../ui/dialog'
import { Button } from '../ui/button'

interface Props {
  images: string[] | string | null
  onClose: () => void
}

const ZOOM_LEVELS = [0.5, 0.75, 1, 1.25, 1.5, 2, 2.5, 3, 4, 5]
const DEFAULT_ZOOM_INDEX = 2 // 1x zoom

const ImageGalleryPreview = ({ images, onClose }: Props) => {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [rotation, setRotation] = useState(0)
  const [zoomIndex, setZoomIndex] = useState(DEFAULT_ZOOM_INDEX)
  const [position, setPosition] = useState({ x: 0, y: 0 })
  const [isDragging, setIsDragging] = useState(false)
  const dragStart = useRef({ x: 0, y: 0 })
  const dragStartPos = useRef({ x: 0, y: 0 })

  const imageArray = Array.isArray(images) ? images : images ? [images] : []
  const hasMultipleImages = imageArray.length > 1
  const currentImage = imageArray[currentIndex]
  const currentZoom = ZOOM_LEVELS[zoomIndex]
  const canDrag = currentZoom > 1

  // Reset to first image and reset transforms when images change
  // biome-ignore lint/correctness/useExhaustiveDependencies: We intentionally want to reset only when images prop changes
  useEffect(() => {
    setCurrentIndex(0)
    setRotation(0)
    setZoomIndex(DEFAULT_ZOOM_INDEX)
    setPosition({ x: 0, y: 0 })
  }, [images])

  // Reset transforms when switching images
  // biome-ignore lint/correctness/useExhaustiveDependencies: We intentionally want to reset only when currentIndex changes
  useEffect(() => {
    setRotation(0)
    setZoomIndex(DEFAULT_ZOOM_INDEX)
    setPosition({ x: 0, y: 0 })
  }, [currentIndex])

  // Reset position when zoom changes to 1x or below
  useEffect(() => {
    if (currentZoom <= 1) {
      setPosition({ x: 0, y: 0 })
    }
  }, [currentZoom])

  const goToNext = useCallback(() => {
    setCurrentIndex(prev => {
      if (prev < imageArray.length - 1) {
        return prev + 1
      }
      return prev
    })
  }, [imageArray.length])

  const goToPrevious = useCallback(() => {
    setCurrentIndex(prev => {
      if (prev > 0) {
        return prev - 1
      }
      return prev
    })
  }, [])

  const rotateLeft = useCallback(() => {
    setRotation(prev => prev - 90 + 360)
  }, [])

  const rotateRight = useCallback(() => {
    setRotation(prev => prev + 90)
  }, [])

  const zoomIn = useCallback(() => {
    setZoomIndex(prev => Math.min(prev + 1, ZOOM_LEVELS.length - 1))
  }, [])

  const zoomOut = useCallback(() => {
    setZoomIndex(prev => Math.max(prev - 1, 0))
  }, [])

  const resetTransforms = useCallback(() => {
    setRotation(0)
    setZoomIndex(DEFAULT_ZOOM_INDEX)
    setPosition({ x: 0, y: 0 })
  }, [])

  // Drag handlers
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (!canDrag) return
      e.preventDefault()
      setIsDragging(true)
      dragStart.current = { x: e.clientX, y: e.clientY }
      dragStartPos.current = { x: position.x, y: position.y }
    },
    [canDrag, position]
  )

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!isDragging) return
      const deltaX = e.clientX - dragStart.current.x
      const deltaY = e.clientY - dragStart.current.y
      setPosition({
        x: dragStartPos.current.x + deltaX,
        y: dragStartPos.current.y + deltaY
      })
    },
    [isDragging]
  )

  const handleMouseUp = useCallback(() => {
    setIsDragging(false)
  }, [])

  const handleMouseLeave = useCallback(() => {
    setIsDragging(false)
  }, [])

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') {
        goToNext()
      } else if (e.key === 'ArrowLeft') {
        goToPrevious()
      } else if (e.key === 'Escape' || e.key === 'Enter') {
        onClose()
      } else if (e.key === 'r' || e.key === 'R') {
        e.shiftKey ? rotateLeft() : rotateRight()
      } else if (e.key === '+' || e.key === '=') {
        zoomIn()
      } else if (e.key === '-' || e.key === '_') {
        zoomOut()
      } else if (e.key === '0') {
        resetTransforms()
      }
    },
    [goToNext, goToPrevious, onClose, rotateLeft, rotateRight, zoomIn, zoomOut, resetTransforms]
  )

  // Set up keyboard event listener
  useEffect(() => {
    if (images && imageArray.length > 0) {
      window.addEventListener('keydown', handleKeyDown)
      return () => {
        window.removeEventListener('keydown', handleKeyDown)
      }
    }
  }, [images, imageArray.length, handleKeyDown])

  return (
    <Dialog open={!!images && imageArray.length > 0} onOpenChange={onClose}>
      <DialogContent className='max-w-[90vw] max-h-[90vh] z-91 bg-white gap-0 border-none'>
        <div className='grid place-items-center mb-2 relative overflow-hidden'>
          {/* biome-ignore lint/a11y/noStaticElementInteractions: This is a drag/pan container for zoomed images */}
          <div
            className='w-full h-[75vh] flex items-center justify-center overflow-hidden'
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseLeave}
            style={{ cursor: canDrag ? (isDragging ? 'grabbing' : 'grab') : 'default' }}
          >
            <img
              className='max-w-full max-h-full object-contain select-none'
              style={{
                transform: `translate(${position.x}px, ${position.y}px) rotate(${rotation}deg) scale(${currentZoom})`,
                transition: isDragging ? 'none' : 'transform 200ms ease'
              }}
              draggable={false}
              src={currentImage ?? ''}
              alt={`Preview ${currentIndex + 1} of ${imageArray.length}`}
            />
          </div>

          {/* Navigation arrows for multiple images */}
          {hasMultipleImages && (
            <>
              <Button
                type='button'
                onClick={goToPrevious}
                disabled={currentIndex === 0}
                className='absolute left-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-3 disabled:opacity-30 disabled:cursor-not-allowed'
                aria-label='Previous image'
              >
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  fill='none'
                  viewBox='0 0 24 24'
                  strokeWidth={2}
                  stroke='currentColor'
                  className='w-6 h-6'
                >
                  <title>Previous</title>
                  <path strokeLinecap='round' strokeLinejoin='round' d='M15.75 19.5L8.25 12l7.5-7.5' />
                </svg>
              </Button>

              <Button
                type='button'
                onClick={goToNext}
                disabled={currentIndex === imageArray.length - 1}
                className='absolute right-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-3 disabled:opacity-30 disabled:cursor-not-allowed'
                aria-label='Next image'
              >
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  fill='none'
                  viewBox='0 0 24 24'
                  strokeWidth={2}
                  stroke='currentColor'
                  className='w-6 h-6'
                >
                  <title>Next</title>
                  <path strokeLinecap='round' strokeLinejoin='round' d='M8.25 4.5l7.5 7.5-7.5 7.5' />
                </svg>
              </Button>
            </>
          )}

          {/* Image counter */}
          {hasMultipleImages && (
            <div className='absolute top-4 right-4 bg-black/50 text-white px-3 py-1 rounded-full text-sm'>
              {currentIndex + 1} / {imageArray.length}
            </div>
          )}

          {/* Zoom and Rotate controls */}
          <div className='absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-1 bg-black/60 backdrop-blur-sm rounded-full px-2 py-1.5'>
            {/* Rotate Left */}
            <Button
              type='button'
              onClick={rotateLeft}
              className='bg-transparent hover:bg-white/20 text-white rounded-full p-2'
              aria-label='Rotate left (Shift+R)'
              title='Rotate left (Shift+R)'
            >
              <svg
                xmlns='http://www.w3.org/2000/svg'
                fill='none'
                viewBox='0 0 24 24'
                strokeWidth={2}
                stroke='currentColor'
                className='w-5 h-5'
              >
                <title>Rotate Left</title>
                <path strokeLinecap='round' strokeLinejoin='round' d='M9 15L3 9m0 0l6-6M3 9h12a6 6 0 010 12h-3' />
              </svg>
            </Button>

            {/* Rotate Right */}
            <Button
              type='button'
              onClick={rotateRight}
              className='bg-transparent hover:bg-white/20 text-white rounded-full p-2'
              aria-label='Rotate right (R)'
              title='Rotate right (R)'
            >
              <svg
                xmlns='http://www.w3.org/2000/svg'
                fill='none'
                viewBox='0 0 24 24'
                strokeWidth={2}
                stroke='currentColor'
                className='w-5 h-5'
              >
                <title>Rotate Right</title>
                <path strokeLinecap='round' strokeLinejoin='round' d='M15 15l6-6m0 0l-6-6m6 6H9a6 6 0 000 12h3' />
              </svg>
            </Button>

            <div className='w-px h-6 bg-white/30 mx-1' />

            {/* Zoom Out */}
            <Button
              type='button'
              onClick={zoomOut}
              disabled={zoomIndex === 0}
              className='bg-transparent hover:bg-white/20 text-white rounded-full p-2 disabled:opacity-30 disabled:cursor-not-allowed'
              aria-label='Zoom out (-)'
              title='Zoom out (-)'
            >
              <svg
                xmlns='http://www.w3.org/2000/svg'
                fill='none'
                viewBox='0 0 24 24'
                strokeWidth={2}
                stroke='currentColor'
                className='w-5 h-5'
              >
                <title>Zoom Out</title>
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  d='M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607zM13.5 10.5h-6'
                />
              </svg>
            </Button>

            {/* Zoom Level Indicator */}
            <span className='text-white text-xs font-medium min-w-[3rem] text-center'>
              {Math.round(currentZoom * 100)}%
            </span>

            {/* Zoom In */}
            <Button
              type='button'
              onClick={zoomIn}
              disabled={zoomIndex === ZOOM_LEVELS.length - 1}
              className='bg-transparent hover:bg-white/20 text-white rounded-full p-2 disabled:opacity-30 disabled:cursor-not-allowed'
              aria-label='Zoom in (+)'
              title='Zoom in (+)'
            >
              <svg
                xmlns='http://www.w3.org/2000/svg'
                fill='none'
                viewBox='0 0 24 24'
                strokeWidth={2}
                stroke='currentColor'
                className='w-5 h-5'
              >
                <title>Zoom In</title>
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  d='M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607zM10.5 7.5v6m3-3h-6'
                />
              </svg>
            </Button>

            <div className='w-px h-6 bg-white/30 mx-1' />

            {/* Reset */}
            <Button
              type='button'
              onClick={resetTransforms}
              disabled={rotation === 0 && zoomIndex === DEFAULT_ZOOM_INDEX && position.x === 0 && position.y === 0}
              className='bg-transparent hover:bg-white/20 text-white rounded-full p-2 disabled:opacity-30 disabled:cursor-not-allowed'
              aria-label='Reset (0)'
              title='Reset (0)'
            >
              <svg
                xmlns='http://www.w3.org/2000/svg'
                fill='none'
                viewBox='0 0 24 24'
                strokeWidth={2}
                stroke='currentColor'
                className='w-5 h-5'
              >
                <title>Reset</title>
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  d='M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99'
                />
              </svg>
            </Button>
          </div>
        </div>

        {/* Thumbnail strip for multiple images */}
        {hasMultipleImages && (
          <div className='flex gap-2 justify-center mb-4 overflow-x-auto max-w-full px-2'>
            {imageArray.map((img, idx) => (
              <button
                type='button'
                key={idx}
                onClick={() => setCurrentIndex(idx)}
                className={`flex-shrink-0 w-16 h-16 rounded border-2 overflow-hidden transition-all ${
                  idx === currentIndex ? 'border-black' : 'border-transparent opacity-60 hover:opacity-100'
                }`}
                aria-label={`View image ${idx + 1}`}
              >
                <img src={img} alt={`Thumbnail ${idx + 1}`} className='w-full h-full object-cover' />
              </button>
            ))}
          </div>
        )}

        <button
          type='button'
          onClick={onClose}
          className='inline-flex p-2 rounded-full items-center justify-center text-black text-sm font-medium leading-[170%] gap-2 hover:underline'
          aria-label='Close preview'
        >
          <CancelIconCircle className='w-4 h-4' color='black' />
          Close preview
        </button>
      </DialogContent>
    </Dialog>
  )
}

export default ImageGalleryPreview
